import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  IconButton,
  Avatar,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  LinearProgress,
  Snackbar
} from '@mui/material';
import {
  Groups as GroupsIcon,
  AccountBalanceWallet as WalletIcon,
  TrendingUp as TrendingUpIcon,
  Person as PersonIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as VisibilityIcon,
  AccountTree as TreeIcon,
  Analytics as AnalyticsIcon,
  MonetizationOn as MoneyIcon,
  CalendarToday as CalendarIcon,
  FilterList as FilterIcon,
  GetApp as DownloadIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

import indicacaoService, {
  EstatisticasIndicacoes,
  ClienteIndicado,
  ArvoreIndicacao,
  HistoricoIndicacao,
  ResumoBonificacoes
} from '../../services/indicacaoService';
import clienteCreditoService, {
  ClienteCredito,
  EstatisticasCreditos
} from '../../services/clienteCreditoService';
import marketingMultinivelService from '../../services/marketingMultinivelService';
import RedeIndicacoesFlow from './RedeIndicacoesFlow';

interface ClientIndicacoesProps {
  clienteId: number;
}

const ClientIndicacoes: React.FC<ClientIndicacoesProps> = ({ clienteId }) => {
  // Estados principais
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<'resumo' | 'arvore' | 'historico'>('resumo');

  // Estados de dados
  const [estatisticas, setEstatisticas] = useState<EstatisticasIndicacoes | null>(null);
  const [creditosEstatisticas, setCreditosEstatisticas] = useState<EstatisticasCreditos | null>(null);
  const [arvoreIndicacoes, setArvoreIndicacoes] = useState<ArvoreIndicacao[]>([]);
  const [creditosDisponiveis, setCreditosDisponiveis] = useState<ClienteCredito[]>([]);
  const [clientesNivel1, setClientesNivel1] = useState<ClienteIndicado[]>([]);
  const [historico, setHistorico] = useState<HistoricoIndicacao[]>([]);
  const [moduloAtivo, setModuloAtivo] = useState(false);

  // Estados de paginação
  const [pageNivel1, setPageNivel1] = useState(0);
  const [limitNivel1] = useState(10);
  const [totalNivel1, setTotalNivel1] = useState(0);

  // Estados de filtros
  const [filtroHistorico, setFiltroHistorico] = useState<{
    tipo?: 'cadastro' | 'recorrente';
    nivel?: number;
  }>({});

  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info'
  });

  useEffect(() => {
    loadData();
  }, [clienteId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Verificar se módulo está ativo
      const ativo = await marketingMultinivelService.isModuleActive();
      setModuloAtivo(ativo);

      if (!ativo) {
        setLoading(false);
        return;
      }

      // Carregar dados em paralelo
      const [
        estatisticasData,
        creditosEstData,
        arvoreData,
        creditosData,
        clientesData,
        historicoData
      ] = await Promise.all([
        indicacaoService.getEstatisticasIndicacoes(clienteId),
        clienteCreditoService.getEstatisticasCreditos(clienteId),
        indicacaoService.getArvoreIndicacoes(clienteId),
        clienteCreditoService.getCreditosDisponiveis(clienteId),
        indicacaoService.getClientesIndicadosNivel1(clienteId, { page: 1, limit: limitNivel1 }),
        indicacaoService.getHistoricoIndicacoes(clienteId, { page: 1, limit: 10 })
      ]);

      setEstatisticas(estatisticasData);
      setCreditosEstatisticas(creditosEstData);
      setArvoreIndicacoes(arvoreData);
      setCreditosDisponiveis(creditosData);
      
      if (clientesData.clientes) {
        setClientesNivel1(clientesData.clientes);
        setTotalNivel1(clientesData.pagination.total);
      }

      if (historicoData.historico) {
        setHistorico(historicoData.historico);
      }

    } catch (err) {
      console.error('Erro ao carregar dados de indicações:', err);
      setError('Erro ao carregar dados de indicações');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message: string, severity: typeof snackbar.severity) => {
    setSnackbar({ open: true, message, severity });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!moduloAtivo) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        <Typography variant="h6" gutterBottom>
          Módulo Marketing Multinível Inativo
        </Typography>
        <Typography>
          O módulo de marketing multinível está desativado. Entre em contato com o administrador para ativá-lo.
        </Typography>
      </Alert>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
        <Button onClick={loadData} sx={{ ml: 2 }}>
          Tentar Novamente
        </Button>
      </Alert>
    );
  }

  return (
    <Box>
      {/* Cabeçalho com navegação */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          <GroupsIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
          Programa de Indicações
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant={activeSection === 'resumo' ? 'contained' : 'outlined'}
            onClick={() => setActiveSection('resumo')}
            startIcon={<AnalyticsIcon />}
            size="small"
          >
            Resumo
          </Button>
          <Button
            variant={activeSection === 'arvore' ? 'contained' : 'outlined'}
            onClick={() => setActiveSection('arvore')}
            startIcon={<TreeIcon />}
            size="small"
          >
            Rede de Indicações
          </Button>

          <Button
            variant={activeSection === 'historico' ? 'contained' : 'outlined'}
            onClick={() => setActiveSection('historico')}
            startIcon={<CalendarIcon />}
            size="small"
          >
            Histórico
          </Button>
        </Box>
      </Box>

      {/* Seção Resumo */}
      {activeSection === 'resumo' && estatisticas && (
        <Grid container spacing={3}>
          {/* Cards de Estatísticas */}
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                    <GroupsIcon />
                  </Avatar>
                  <Box>
                    <Typography color="text.secondary" variant="body2">
                      Total de Indicações
                    </Typography>
                    <Typography variant="h5">
                      {estatisticas.totalIndicacoes}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                    <GroupsIcon />
                  </Avatar>
                  <Box>
                    <Typography color="text.secondary" variant="body2">
                      Indicações Diretas
                    </Typography>
                    <Typography variant="h5">
                      {estatisticas.indicacoesNivel1}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Distribuição por Níveis */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Indicações por Nível
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Nível 1</Typography>
                    <Typography variant="body2">{estatisticas.indicacoesNivel1}</Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={estatisticas.totalIndicacoes > 0 ? (estatisticas.indicacoesNivel1 / estatisticas.totalIndicacoes) * 100 : 0}
                    sx={{ mb: 2 }}
                  />
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Nível 2</Typography>
                    <Typography variant="body2">{estatisticas.indicacoesNivel2}</Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={estatisticas.totalIndicacoes > 0 ? (estatisticas.indicacoesNivel2 / estatisticas.totalIndicacoes) * 100 : 0}
                    sx={{ mb: 2 }}
                  />
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Nível 3</Typography>
                    <Typography variant="body2">{estatisticas.indicacoesNivel3}</Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={estatisticas.totalIndicacoes > 0 ? (estatisticas.indicacoesNivel3 / estatisticas.totalIndicacoes) * 100 : 0}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Seção Árvore de Indicações */}
      {activeSection === 'arvore' && (
        <RedeIndicacoesFlow
          arvoreIndicacoes={arvoreIndicacoes}
          clienteId={clienteId}
          clienteNome="Cliente Atual" // TODO: Obter nome real do cliente
        />
      )}

      {/* Seção Histórico */}
      {activeSection === 'historico' && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Histórico de Bonificações
            </Typography>
            {historico.length === 0 ? (
              <Alert severity="info">
                Nenhuma bonificação encontrada.
              </Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Data</TableCell>
                      <TableCell>Tipo</TableCell>
                      <TableCell>Cliente</TableCell>
                      <TableCell>Nível</TableCell>
                      <TableCell>Valor</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {historico.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{indicacaoService.formatDateTime(item.data_processamento)}</TableCell>
                        <TableCell>
                          <Chip
                            label={indicacaoService.formatTipoBonificacao(item.tipo_bonificacao)}
                            color={indicacaoService.getTipoBonificacaoColor(item.tipo_bonificacao)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{item.cliente_indicado?.nome || 'N/A'}</TableCell>
                        <TableCell>Nível {item.nivel}</TableCell>
                        <TableCell>{indicacaoService.formatCurrency(item.valor_bonificacao)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ClientIndicacoes; 